import { useNinetailed, useProfile } from '@ninetailed/experience.js-next';
import {
  AgeSegmentationIds,
  AllAllergySegmentationIds,
  BreedSegmentationIds,
  CatNutritionSegmentationIds,
  NutritionSegmentationIds,
  SegmentationData,
  SegmentationIds,
  isAllergySegmentation,
} from 'constants/segmentation';
import useIsAdultRelated from 'hooks/common/use-is-adult-related';
import { useRouter } from 'next/router';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { useConfig } from 'config/use-config';
import { isEmpty } from 'lodash';
import { useUserSegmentationStore } from 'stores/user-segmentation';
import convertSegmentationToArray from 'utils/segmentation/convert-segmentation-to-array';
import mergeSegmentationStores from 'utils/segmentation/merge-segmentaion-stores';
import updateSegmentationStore from 'utils/segmentation/update-segmentation-store';
import { isEmptyObject } from 'utils/type-helper';
import { isTraits, toSegmentList } from '../utils/ninetailed';
import { useAuth } from './auth';
import { getSegmentationFromUrl } from 'utils/url/get-segmentation-from-url';
import useSyncSegmentation from 'hooks/pet-profile/useSyncSegmentation';
import { useRootStore } from 'stores/use-root-store';
import convertProfileToArray from 'utils/pet-profile/convert-profile-to-array';

export interface SegmentationContextType {
  segmentationData: typeof SegmentationData;
  /** Method to push segmentation */
  pushSegmentation: (arg: {
    segmentation: string | SegmentationStore;
    /** true if segmentation is from user action, such as clicking on a link or update filter*/
    isUserAction?: boolean;
  }) => void;
  /** Method to remove segmentation */
  removeSegmentation: (arg: { segmentation: string }) => void;
  /** Current selected segmentation. it's stored in local storage to bridge sessions */
  multipleSegmentation?: SegmentationStore;
  /** Last pushed segmentation, when only one segmentation matter, value should be taken from here  */
  lastSegmentation?: SegmentationIds;
  /** segmentation state. Based on set up, it could be single or multiple segmentation */
  segmentation?: SegmentationIds | SegmentationStore;
  /** true if segmentation data is being validated  */
  isSegmentationReady?: boolean;
  /** true if personalization should be omitted e.g welpe personalization on adult page */
  shouldOmitPersonalization: boolean;
  /** Method to set all pet segments */
  setAllPetSegments: React.Dispatch<React.SetStateAction<SegmentationIds[]>>;
  /** All pet segments */
  allPetSegments: SegmentationIds[];
}

const SegmentationContext = createContext<SegmentationContextType>({} as any);

export interface SegmentationStore {
  breedSegmentation?: BreedSegmentationIds;
  /** Segmentation store for multiple need */
  needSegmentation?: Array<NutritionSegmentationIds | CatNutritionSegmentationIds>;
  ageSegmentation?: AgeSegmentationIds;
  /** Segmentation store for multiple allergies */
  allergySegmentation?: Array<AllAllergySegmentationIds>;
  lastSegmentation?: SegmentationIds;
}

/**
 * Context to share pet needs segmentation through app and take care of updating it
 */
export const SegmentationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { featureFlags } = useConfig();
  const { asPath, isReady } = useRouter();
  const { addSegment, removeSegment } = useUserSegmentationStore();

  const [localSegmentationStore, setLocalSegmentationStore] = useLocalStorage<SegmentationStore>(
    'pd:segmentationStore',
    {}
  );

  const [segmentationStoreState, setSegmentationStoreState] = useState<SegmentationStore>(
    localSegmentationStore || {}
  );

  const [allPetSegments, setAllPetSegments] = useState<SegmentationIds[]>([]);

  const customerPets = useRootStore((store) => store.customerPets);
  const currentPetProfile = useRootStore((store) => store.currentPetProfile);

  const { identify } = useNinetailed();
  const { profile, loading } = useProfile();
  const { customer } = useAuth();
  const featureFlag = featureFlags.segmentation;

  // Union across saved pets; feeds `allSegments` and GA, not the identify petProfile trait below.
  useEffect(() => {
    const segments = customerPets.flatMap((pet) =>
      convertProfileToArray({ petProfile: pet })
    ) as SegmentationIds[];
    const unique = Array.from(new Set(segments)).sort();
    setAllPetSegments((prev) =>
      prev.length === unique.length && prev.every((s, i) => s === unique[i]) ? prev : unique
    );
  }, [customerPets]);

  const pushSegmentation: SegmentationContextType['pushSegmentation'] = useCallback(
    ({ segmentation, isUserAction = false }) => {
      if (!featureFlag) {
        return;
      }

      if (isEmpty(segmentation)) {
        console.warn('empty segmentation');
        return;
      }

      if (typeof segmentation !== 'string') {
        setSegmentationStoreState(segmentation);
        return;
      }

      // Add to user segmentation store if it's a user action
      if (isUserAction) {
        addSegment(segmentation as SegmentationIds);
      }

      const _setSegmentationStoreState = (segmentation) =>
        setSegmentationStoreState((prev) => {
          return updateSegmentationStore({
            existingStore: {
              ...prev,
              // omit allergy
              ...(!isAllergySegmentation(segmentation as SegmentationIds)
                ? { lastSegmentation: segmentation }
                : {}),
            },
            segmentation,
          });
        });

      _setSegmentationStoreState(segmentation);
    },
    [featureFlag, addSegment]
  );

  const removeSegmentation: SegmentationContextType['removeSegmentation'] = useCallback(
    ({ segmentation }) => {
      // Remove from user segmentation store
      removeSegment(segmentation as SegmentationIds);

      const _setSegmentationStoreState = (segmentation) =>
        setSegmentationStoreState((prev) => {
          return updateSegmentationStore({
            existingStore: { ...prev },
            segmentation,
            mode: 'remove',
          });
        });

      _setSegmentationStoreState(segmentation);
    },
    [removeSegment]
  );

  /** Sync with local state */
  useEffect(() => {
    setLocalSegmentationStore(segmentationStoreState);
  }, [segmentationStoreState, setLocalSegmentationStore]);

  /**
   * Sync segmentation to Ninetailed
   */
  useEffect(() => {
    const segmentationSegments = convertSegmentationToArray({
      store: segmentationStoreState,
      shouldOmitLastSegment: true,
    });

    // Create a Set to automatically handle deduplication
    const uniqueSegments = new Set([...segmentationSegments, ...allPetSegments]);

    // Joined rather than sent as arrays: Ninetailed does not coerce array trait values
    // to their string equivalent, so `contains` rules never match one.
    identify(`${customer ? customer.customerId : ''}`, {
      allSegments: Array.from(uniqueSegments).sort().join(','),
      petProfile: convertProfileToArray({ petProfile: currentPetProfile }).sort().join(','),
      ...(segmentationStoreState.lastSegmentation
        ? { lastSegment: segmentationStoreState.lastSegmentation }
        : {}),
    });
  }, [customer, identify, segmentationStoreState, allPetSegments, currentPetProfile]);

  /**
   * Initial state set up from localStorage or Ninetailed trails
   */
  const triggerInitialPush = useRef(false);
  useEffect(() => {
    if (!isReady || triggerInitialPush.current || loading) return;
    triggerInitialPush.current = true;

    let initialSegmentationState = {};

    const _segmentation = getSegmentationFromUrl({ path: asPath });
    if (_segmentation) {
      initialSegmentationState = updateSegmentationStore({
        segmentation: _segmentation.id as SegmentationIds,
        existingStore: initialSegmentationState,
      });
    }

    if (localSegmentationStore && !isEmptyObject(localSegmentationStore)) {
      initialSegmentationState = mergeSegmentationStores({
        target: initialSegmentationState,
        source: localSegmentationStore,
      });
    } else if (profile && isTraits(profile.traits) && profile.traits.allSegments) {
      let segmentationStore = {};
      toSegmentList(profile.traits.allSegments).forEach((seg) => {
        segmentationStore = updateSegmentationStore({
          segmentation: seg as SegmentationIds,
          existingStore: segmentationStore,
        });
      });
      initialSegmentationState = mergeSegmentationStores({
        target: initialSegmentationState,
        source: segmentationStore,
      });
    }

    // Initialize with user action false since these are from storage/profile
    pushSegmentation({
      segmentation: initialSegmentationState,
      isUserAction: false,
    });
  }, [
    asPath,
    isReady,
    loading,
    localSegmentationStore,
    profile,
    pushSegmentation,
    segmentationStoreState,
  ]);

  const { shouldOmitPersonalization } = useIsAdultRelated({
    segmentation: segmentationStoreState,
  });

  useSyncSegmentation({
    multipleSegmentation: segmentationStoreState,
    pushSegmentation,
    removeSegmentation,
  });

  const value = React.useMemo(() => {
    return {
      segmentationData: SegmentationData,
      multipleSegmentation: segmentationStoreState,
      lastSegmentation: segmentationStoreState.lastSegmentation,
      pushSegmentation,
      removeSegmentation,
      isSegmentationReady: true,
      shouldOmitPersonalization,
      segmentation: segmentationStoreState,
      setAllPetSegments,
      allPetSegments,
    };
  }, [
    segmentationStoreState,
    shouldOmitPersonalization,
    pushSegmentation,
    removeSegmentation,
    setAllPetSegments,
    allPetSegments,
  ]);

  return <SegmentationContext.Provider value={value}>{children}</SegmentationContext.Provider>;
};

export const useSegmentationContext = () => useContext(SegmentationContext);
